import { createContext, useContext, useState, useCallback } from 'react';

const PricingContext = createContext(null);

// Default package used whenever a lead is submitted without selecting
// a pricing package first (hero forms, header/footer popups, etc.)
export const DEFAULT_PACKAGE = {
  packageTitle: 'Basic Package',
  packageAmount: '$45',
  tmSignUpType: 1
};

export const PRICING_TABS = {
  SELF: 'self',
  ATTORNEY: 'attorney'
};

const TAB_TO_SIGNUP_TYPE = {
  [PRICING_TABS.SELF]: 1,
  [PRICING_TABS.ATTORNEY]: 2
};

export const PricingProvider = ({ children }) => {
  const [activePricingTab, setActivePricingTabState] = useState(PRICING_TABS.SELF);
  const [selectedPackage, setSelectedPackageState] = useState(null);

  const tmSignUpType = TAB_TO_SIGNUP_TYPE[activePricingTab];

  const setActivePricingTab = useCallback((tab) => {
    setActivePricingTabState(tab);
  }, []);

  // Called when a user clicks a pricing card's "Get Started" CTA
  const setSelectedPackage = useCallback((pkg) => {
    const packageTitle = pkg?.packageTitle || pkg?.name || '';
    const packageAmount = pkg?.packageAmount || pkg?.price || '';
    const signUpType = TAB_TO_SIGNUP_TYPE[activePricingTab];

    const next = {
      packageTitle,
      packageAmount,
      tmSignUpType: signUpType
    };

    setSelectedPackageState(next);

    // Temporary dev verification logs - remove before production deployment.
    if (signUpType === PRICING_TABS.ATTORNEY || signUpType === 2) {
      console.log({ tmSignUpType: 2, packageTitle, packageAmount });
    } else {
      console.log({ tmSignUpType: 1, packageTitle, packageAmount });
    }

    return next;
  }, [activePricingTab]);

  const clearSelectedPackage = useCallback(() => {
    setSelectedPackageState(null);
  }, []);

  // Resolves the package info that should accompany any lead submission.
  // Falls back to the default package when nothing has been explicitly selected.
  const getPackageForSubmission = useCallback(() => {
    if (selectedPackage) return selectedPackage;
    return { ...DEFAULT_PACKAGE };
  }, [selectedPackage]);

  const value = {
    activePricingTab,
    setActivePricingTab,
    selectedPackage,
    setSelectedPackage,
    clearSelectedPackage,
    tmSignUpType,
    getPackageForSubmission,
    DEFAULT_PACKAGE
  };

  return (
    <PricingContext.Provider value={value}>
      {children}
    </PricingContext.Provider>
  );
};

export const usePricing = () => {
  const ctx = useContext(PricingContext);
  if (!ctx) {
    throw new Error('usePricing must be used within a PricingProvider');
  }
  return ctx;
};

export default PricingContext;
